import * as React from 'react';
import { BLACKS_AND_WHITES } from '../../utils/DataFormatterUtils';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { Popover } from 'antd';
import {
	getWalkthroughHeatMapColorsForPercentage,
	getWalkthroughHeatMapRangesForWalkthrough,
	parseScorePercentage,
} from '../../utils/WalkthroughHeatMapRangeUtils';

// Colors come from the score's own site survey, so a customer that configured custom ranges sees the
// same thresholds here as on its walkthrough heat map. `walkthrough` is optional — without it (or
// on a search-backed row, whose OpenSearch doc does not carry the ranges) this resolves to the
// platform defaults.
const getScoreColors = (scoreObject, walkthrough) => {
	const percentageScore = parseScorePercentage(nullSafeGet('percentageScore', scoreObject));
	const heatMapRanges = getWalkthroughHeatMapRangesForWalkthrough(walkthrough);
	const colors = getWalkthroughHeatMapColorsForPercentage(percentageScore, heatMapRanges);
	return { statusColor: colors.text, statusBackgroundColor: colors.background };
};

const getScoreMessage = (score, minPossibleScore, maxPossibleScore) =>
	minPossibleScore === 0
		? `Scored ${score} out of ${maxPossibleScore} possible points`
		: `Scored ${score} on a ${minPossibleScore} to ${maxPossibleScore} scale`;

const WalkthroughScoreDisplay = ({
	scoreObject,
	withPrefix = false,
	style = {},
	isPill = false,
	walkthrough = null,
}) => {
	const score = nullSafeGet('score', scoreObject);
	const maxPossibleScore = nullSafeGet('maxPossibleScore', scoreObject);
	const minPossibleScore = nullSafeGetOrElse('minPossibleScore', scoreObject, 0);
	const percentageScore = nullSafeGet('percentageScore', scoreObject);

	if ((!score || !maxPossibleScore) && isPill) return null;

	const { statusColor, statusBackgroundColor } = getScoreColors(scoreObject, walkthrough);
	return (
		<div className="flex flex-col">
			<span
				style={{
					...style,
					...(isPill && {
						background: statusBackgroundColor,
						padding: '4px 12px',
						borderRadius: 16,
					}),
				}}
			>
				{score && maxPossibleScore ? (
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
						}}
					>
						<Popover
							content={<div>{getScoreMessage(score, minPossibleScore, maxPossibleScore)}</div>}
						>
							<div
								style={{
									minWidth: 10,
									margin: 0,
								}}
							>
								{withPrefix ? <span className="h5regular mr-2 text-[#4B5563]">Score:</span> : null}
								<span className="h5regular" style={{ marginRight: 2, color: statusColor }}>
									{percentageScore}%
								</span>
							</div>
						</Popover>
					</div>
				) : null}
			</span>
		</div>
	);
};

export default WalkthroughScoreDisplay;

export const WalkthroughScoreLegend = ({ scoreObject, style = {}, walkthrough = null }) => {
	const score = nullSafeGet('score', scoreObject);
	const maxPossibleScore = nullSafeGet('maxPossibleScore', scoreObject);
	const minPossibleScore = nullSafeGetOrElse('minPossibleScore', scoreObject, 0);

	const { statusColor, statusBackgroundColor } = getScoreColors(scoreObject, walkthrough);
	return (
		<span
			style={{
				color: statusColor,
				...style,
			}}
		>
			{score && maxPossibleScore ? (
				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
					}}
				>
					<Popover
						content={<div>{getScoreMessage(score, minPossibleScore, maxPossibleScore)}</div>}
					>
						<div
							style={{
								minWidth: 10,
								margin: 0,
							}}
						>
							<div
								className={`h-4 w-4 rounded transition-transform duration-100 hover:scale-105 `}
								style={{
									backgroundColor: statusBackgroundColor,
								}}
							/>
						</div>
					</Popover>
				</div>
			) : (
				<span style={{ color: BLACKS_AND_WHITES.blackSecondary }}>-/-</span>
			)}
		</span>
	);
};
